<template>
<div class="container-fluid p-0">
    <div class="row">
        <CCol sm="12">
            <CCard>
                <CCardBody>
                    <div class="d-flex mb-4 justify-content-between align-items-center">
                        <!-- Title -->
                        <span v-if="name"><b>{{ $t('experiences.experienceIntro') }}</b> {{ plainName }}</span>
                        <!-- Language dropdown -->
                        <LanguageDropdown v-model="currentLanguage"></LanguageDropdown>
                    </div>

                    <!-- Without upcoming time slots the experience cannot be booked on the website -->
                    <div v-if="experienceId && hasFutureSlots === false" class="alert alert-warning no-slots-alert">
                        <span><i class="fas fa-exclamation-triangle mr-2"></i>{{ $t('experiences.noFutureSlots') }}</span>
                        <a :href="createSlotsHref" target="_blank" rel="noopener" class="btn btn-sm btn-warning">
                            {{ $t('pricePeriods.createSlots') }} <i class="fas fa-external-link-alt ml-1"></i>
                        </a>
                    </div>
                    
                    <div class="experience-form-container">
                        <!-- Left side - Experience Images -->
                        <div class="experience-image-section">
                            <div role="group" class="form-group">
                                <label>{{ $t('experiences.images') }}</label>
                                <GalleryPicker 
                                    class="gallery-picker" 
                                    v-model="experience.images" 
                                    :ratioWidth="16" 
                                    :ratioHeight="9" 
                                    v-bind:class="{'is-invalid': showValidation && !genericValidator(experience.images) }" 
                                    :max="10">
                                </GalleryPicker>
                                <div v-if="errorOutput['Images']" class="invalid-feedback">{{errorOutput['Images']}}</div>
                            </div>
                        </div>

                        <!-- Right side - Form Fields -->
                        <div class="experience-form-section">
                            <div class="row">
                                <!-- Position -->
                                <div class="col-12 mb-4">
                                    <div role="group" class="form-group">
                                        <label>{{ $t('positionOptional') }}</label>
                                        <CInput 
                                            type="number" 
                                            v-model="experience.position" 
                                            :invalid-feedback="errorOutput['Position']"
                                            :is-valid="errorValidator('Position')">
                                        </CInput>
                                    </div>
                                </div>

                                <!-- Names -->
                                <div class="col-12 mb-4">
                                    <div role="group" class="form-group">
                                        <label>{{ $t('nameMandatory') }}</label>
                                        <VueEditor 
                                            v-model="experience.names[currentLanguage.key]" 
                                            v-bind:class="{'is-invalid': showValidation && !genericValidator(experience.names) }">
                                        </VueEditor>
                                        <div v-if="errorOutput['Names']" class="invalid-feedback">{{errorOutput['Names']}}</div>
                                    </div>
                                </div>

                                <!-- Short Descriptions -->
                                <div class="col-12 mb-4">
                                    <div role="group" class="form-group">
                                        <label>{{ $t('experiences.shortDescriptionMandatory') }}</label>
                                        <VueEditor 
                                            v-model="experience.shortDescriptions[currentLanguage.key]">
                                        </VueEditor>
                                        <div v-if="errorOutput['ShortDescriptions']" class="invalid-feedback">{{errorOutput['ShortDescriptions']}}</div>
                                    </div>
                                </div>

                                <!-- Descriptions -->
                                <div class="col-12 mb-4">
                                    <div role="group" class="form-group">
                                        <label>{{ $t('experiences.descriptionMandatory') }}</label>
                                        <VueEditor 
                                            v-model="experience.descriptions[currentLanguage.key]" 
                                            v-bind:class="{'is-invalid': showValidation && !genericValidator(experience.descriptions) }">
                                        </VueEditor>
                                        <div v-if="errorOutput['Descriptions']" class="invalid-feedback">{{errorOutput['Descriptions']}}</div>
                                    </div>
                                </div>

                                <!-- Featured Checkbox -->
                                <div class="col-12 col-lg-6 mb-4">
                                    <div role="group" class="form-group">
                                        <div class="form-check">
                                            <input 
                                                type="checkbox" 
                                                id="isFeatured" 
                                                v-model="experience.isFeatured"
                                                class="form-check-input">
                                            <label class="form-check-label" for="isFeatured">
                                                {{ $t('experiences.featured') }}
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <!-- Pre-payment Checkbox -->
                                <div class="col-12 col-lg-6 mb-4">
                                    <div role="group" class="form-group">
                                        <div class="form-check">
                                            <input 
                                                type="checkbox" 
                                                id="needsPrePayment" 
                                                v-model="experience.needsPrePayment"
                                                class="form-check-input">
                                            <label class="form-check-label" for="needsPrePayment">
                                                {{ $t('experiences.needsPrePayment') }}
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <!-- Pre-payment Percentage (only shown when needsPrePayment is checked) -->
                                <div v-if="experience.needsPrePayment" class="col-12 col-lg-6 mb-4">
                                    <div role="group" class="form-group">
                                        <label>{{ $t('experiences.prePaymentPercentage') }} (%)</label>
                                        <CInput 
                                            type="number" 
                                            step="1"
                                            min="1"
                                            max="100"
                                            v-model="experience.prePaymentPercentage"
                                            placeholder="100">
                                        </CInput>
                                    </div>
                                </div>

                                <!-- Price Type -->
                                <div class="col-12 col-lg-6 mb-4">
                                    <div role="group" class="form-group">
                                        <label>{{ $t('experiences.priceType') }}</label>
                                        <select 
                                            class="form-control" 
                                            v-model="experience.priceType"
                                            :disabled="pricePeriodCount > 0"
                                            :class="{ 'is-invalid': errorOutput['PriceType'] }"
                                        >
                                            <option value="PerPerson">{{ $t('experiences.priceTypePerPerson') }}</option>
                                            <option value="PerExperience">{{ $t('experiences.priceTypePerExperience') }}</option>
                                        </select>
                                        <small v-if="pricePeriodCount > 0" class="form-text text-muted">
                                            {{ $t('experiences.priceTypeLockedByPeriods') }}
                                        </small>
                                        <div v-if="errorOutput['PriceType']" class="invalid-feedback">
                                            {{ errorOutput['PriceType'] }}
                                        </div>
                                    </div>
                                </div>

                                <!-- Price (only shown when PriceType is PerExperience) -->
                                <div v-if="experience.priceType === 'PerExperience'" class="col-12 col-lg-6 mb-4">
                                    <div role="group" class="form-group">
                                        <label>{{ $t('experiences.price') }} (€)</label>
                                        <CInput 
                                            type="number" 
                                            step="0.01"
                                            min="0"
                                            v-model="experience.price" 
                                            :invalid-feedback="errorOutput['Price']"
                                            :is-valid="experience.price && experience.price > 0 ? errorValidator('Price') : null"
                                            placeholder="0.00">
                                        </CInput>
                                    </div>
                                </div>

                                <!-- Age Bracket Prices Table (only shown when PriceType is PerPerson) -->
                                <div v-if="experience.priceType === 'PerPerson'" class="col-12 mb-4">
                                    <div role="group" class="form-group">
                                        <label>{{ $t('experiences.ageBracketPrices') }}</label>
                                        <div class="table-responsive">
                                            <table class="table table-bordered">
                                                <thead>
                                                    <tr>
                                                        <th>{{ $t('experiences.ageBracket') }}</th>
                                                        <th>{{ $t('experiences.price') }} (€)</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr v-for="bracket in ageBrackets" :key="bracket.key">
                                                        <td>{{ bracket.label }}</td>
                                                        <td>
                                                            <CInput 
                                                                type="number" 
                                                                step="0.01"
                                                                min="0"
                                                                v-model="experience.ageBracketPrices[bracket.key]"
                                                                placeholder="0.00"
                                                            />
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Form Actions -->
                    <div class="form-actions mt-4">
                        <CButton 
                            color="primary" 
                            @click="submitForm" 
                            :disabled="loading"
                        >
                            <span v-if="loading" class="spinner-border spinner-border-sm mr-2"></span>
                            {{ $t('save') }}
                        </CButton>
                        <CButton 
                            color="secondary" 
                            @click="cancelForm" 
                            class="ml-2"
                        >
                            {{ $t('cancel') }}
                        </CButton>
                    </div>

                    <!-- Price periods (saved on their own, only for an existing experience) -->
                    <ExperiencePricePeriods
                        v-if="experienceId"
                        :experienceId="experienceId"
                        :priceType="savedPriceType || experience.priceType"
                        :baseAgeBracketPrices="experience.ageBracketPrices"
                        @count-changed="onPricePeriodCountChanged">
                    </ExperiencePricePeriods>
                </CCardBody>
            </CCard>
        </CCol>
    </div>
    <ConfirmDialog
      :title="englishValidationTitle"
      :message="englishValidationMessage"
      v-model="showEnglishValidationDialog"
      @confirm="onCloseEnglishValidationDialog"
      @cancel="onCloseEnglishValidationDialog"
    ></ConfirmDialog>
</div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import { VueEditor } from "vue2-editor";
import { CInput, CButton, CCol, CCard, CCardBody } from '@coreui/vue';
import LanguageDropdown from '@/controls/LanguageDropdown.vue';
import GalleryPicker from '@/controls/GalleryPicker.vue';
import ConfirmDialog from '@/controls/popups/ConfirmDialog.vue';
import ExperiencePricePeriods from './ExperiencePricePeriods.vue';
import { pricePeriodsService } from '@/services/pricePeriods.service';
import { toIsoDate } from '@/helpers/prices';
import router from '@/router';
import { CONSTANTS } from '@/helpers/consts';

export default {
  name: 'ExperiencesForm',
  components: {
    VueEditor,
    CInput,
    CButton,
    CCol,
    CCard,
    CCardBody,
    LanguageDropdown,
    GalleryPicker,
    ConfirmDialog,
    ExperiencePricePeriods
  },
  props: {
    experienceId: {
      type: [String, Number],
      default: null
    }
  },
  data() {
    return {
      currentLanguage: CONSTANTS.supportedLanguages[0],
      loading: false,
      showValidation: false, // Controla quando mostrar validação visual
      showEnglishValidationDialog: false,
      englishValidationMessage: '',
      englishValidationTitle: '',
      ageBrackets: [
        { key: 'ZeroToFive', label: '0-5' },
        { key: 'SixToEleven', label: '6-11' },
        { key: 'TwelveToSeventeen', label: '12-17' },
        { key: 'Adult', label: this.$t('experiences.ageBracketAdult') },
        { key: 'Senior', label: '65+' }
      ],
      experience: {
        position: 999,
        isFeatured: false,
        needsPrePayment: false,
        prePaymentPercentage: 100,
        price: 0,
        priceType: 'PerExperience',
        ageBracketPrices: {
          'ZeroToFive': 0,
          'SixToEleven': 0,
          'TwelveToSeventeen': 0,
          'Adult': 0,
          'Senior': 0
        },
        names: {
          'pt': '',
          'en': ''
        },
        shortDescriptions: {
          'pt': '',
          'en': ''
        },
        descriptions: {
          'pt': '',
          'en': ''
        },
        images: []
      },
      errorOutput: {},
      // Price type as stored: periods are priced in it, and it cannot change while periods exist
      savedPriceType: null,
      pricePeriodCount: 0,
      // null while unknown, so the warning never flashes before the check
      hasFutureSlots: null
    }
  },
  computed: {
    // New time-slot interval for this experience, opened in another tab
    createSlotsHref() {
      return this.$router.resolve({ name: 'AvailabilityIntervalsAdd', query: { experienceId: String(this.experienceId) } }).href;
    },
    ...mapState('experiences', ['getExperienceByIdOutput']),
    name() {
      return this.experience.names[this.currentLanguage.key] || '';
    },
    plainName() {
      const raw = this.experience.names[this.currentLanguage.key] || '';
      if (typeof raw !== 'string') return '';
      return raw.replace(/<[^>]*>/g, '').trim();
    }
  },
  watch: {
    // The form is reused when only ?id= changes: load the new experience, or saving would overwrite it with the old one
    experienceId(newValue) {
      if (newValue) {
        this.savedPriceType = null;
        this.pricePeriodCount = 0;
        this.hasFutureSlots = null;
        this.errorOutput = {};
        this.showValidation = false;
        this.loadExperience();
        this.checkFutureSlots();
      }
    },
    'experience.priceType'(newValue) {
      // When switching to PerPerson, clear the price field
      if (newValue === 'PerPerson') {
        this.experience.price = 0;
      }
    }
  },
  async mounted() {
    if (this.experienceId) {
      await this.loadExperience();
      this.checkFutureSlots();
    }
    // Slots are created in another tab: re-check when the admin comes back
    window.addEventListener('focus', this.checkFutureSlots);
  },
  beforeDestroy() {
    window.removeEventListener('focus', this.checkFutureSlots);
  },
  methods: {
    ...mapActions('experiences', ['getById', 'create', 'update']),
    
    async loadExperience() {
      try {
        this.loading = true;
        await this.getById(this.experienceId);
        
        if (this.getExperienceByIdOutput && this.getExperienceByIdOutput.data) {
          const data = this.getExperienceByIdOutput.data;
          
          // Initialize age bracket prices from API or defaults
          const ageBracketPrices = {
            'ZeroToFive': 0,
            'SixToEleven': 0,
            'TwelveToSeventeen': 0,
            'Adult': 0,
            'Senior': 0
          };
          
          if (data.ageBracketPrices) {
            Object.keys(data.ageBracketPrices).forEach(key => {
              ageBracketPrices[key] = data.ageBracketPrices[key] || 0;
            });
          }
          
          // If PriceType is PerPerson, ignore the price field (set to 0)
          const priceValue = (data.priceType === 'PerPerson') ? 0 : (data.price || 0);
          
          this.experience = {
            position: data.position || 999,
            isFeatured: data.isFeatured || false,
            needsPrePayment: data.needsPrePayment || false,
            prePaymentPercentage: data.prePaymentPercentage || 100,
            price: priceValue,
            priceType: data.priceType || 'PerExperience',
            ageBracketPrices: ageBracketPrices,
            names: data.names || { 'pt': '', 'en': '' },
            shortDescriptions: data.shortDescriptions || { 'pt': '', 'en': '' },
            descriptions: data.descriptions || { 'pt': '', 'en': '' },
            images: data.images || []
          };
          this.savedPriceType = this.experience.priceType;
        }
      } catch (error) {
        console.error('Error loading experience:', error);
        this.$store.dispatch('alert/setAlert', {
          type: 'error',
          message: this.$t('experiences.loadError')
        });
      } finally {
        this.loading = false;
      }
    },
    
    async checkFutureSlots() {
      const experienceId = this.experienceId;
      if (!experienceId) return;
      const today = new Date();
      const until = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 730);
      try {
        const schedule = await pricePeriodsService.getSchedule(experienceId, toIsoDate(today), toIsoDate(until));
        if (experienceId !== this.experienceId) return;
        this.hasFutureSlots = (schedule && schedule.days ? schedule.days : []).some(day => day.slotCount > 0);
      } catch (error) {
        // Unknown: show nothing rather than a false warning
        this.hasFutureSlots = null;
      }
    },

    onPricePeriodCountChanged(count) {
      this.pricePeriodCount = count;
      // An unsaved price type change cannot be kept once the experience has periods
      if (count > 0 && this.savedPriceType) {
        this.experience.priceType = this.savedPriceType;
      }
    },

    async submitForm() {
      try {
        this.loading = true;
        this.errorOutput = {};
        this.showValidation = true; // Ativar validação visual quando clicar em save
        
        // Validate form
        if (!this.validateForm()) {
          // Show pop-up if Portuguese or English translation is missing when creating
          if (!this.experienceId) {
            const isEmptyHtml = (html) => {
              if (!html || typeof html !== 'string') return true;
              const textContent = html.replace(/<[^>]*>/g, '').trim();
              return textContent === '';
            };
            
            const portugueseNameEmpty = !this.experience.names['pt'] || isEmptyHtml(this.experience.names['pt']);
            const englishNameEmpty = !this.experience.names['en'] || isEmptyHtml(this.experience.names['en']);
            const portugueseDescriptionEmpty = !this.experience.descriptions['pt'] || isEmptyHtml(this.experience.descriptions['pt']);
            const englishDescriptionEmpty = !this.experience.descriptions['en'] || isEmptyHtml(this.experience.descriptions['en']);
            
            // Determine what's missing
            const missingPortuguese = portugueseNameEmpty || portugueseDescriptionEmpty;
            const missingEnglish = englishNameEmpty || englishDescriptionEmpty;
            
            if (missingPortuguese || missingEnglish) {
              // Build detailed message based on what's missing
              const missingFields = [];
              let message = '';
              let title = '';
              
              if (missingPortuguese && missingEnglish) {
                // Both languages missing
                title = this.$t('validation.required');
                if (portugueseNameEmpty || englishNameEmpty) {
                  missingFields.push(this.$t('nameMandatory'));
                }
                if (portugueseDescriptionEmpty || englishDescriptionEmpty) {
                  missingFields.push(this.$t('experiences.descriptionMandatory'));
                }
                message = this.$t('validation.required') + 
                  (missingFields.length > 0 ? ': ' + missingFields.join(', ') : '');
              } else if (missingPortuguese) {
                // Only Portuguese missing
                title = this.$t('validation.required');
                if (portugueseNameEmpty) {
                  missingFields.push(this.$t('nameMandatory') + ' (PT)');
                }
                if (portugueseDescriptionEmpty) {
                  missingFields.push(this.$t('experiences.descriptionMandatory') + ' (PT)');
                }
                message = this.$t('validation.required') + 
                  (missingFields.length > 0 ? ': ' + missingFields.join(', ') : '');
              } else if (missingEnglish) {
                // Only English missing
                title = this.$t('validation.englishRequiredTitle');
                if (englishNameEmpty) {
                  missingFields.push(this.$t('nameMandatory'));
                }
                if (englishDescriptionEmpty) {
                  missingFields.push(this.$t('experiences.descriptionMandatory'));
                }
                message = this.$t('validation.englishRequired') + 
                  (missingFields.length > 0 ? ': ' + missingFields.join(', ') : '');
              }
              
              this.englishValidationTitle = title;
              this.englishValidationMessage = message;
              this.showEnglishValidationDialog = true;
            }
          }
          this.loading = false;
          return;
        }
        
        const formData = {
          ...this.experience,
          position: parseInt(this.experience.position) || 999
        };
        
        // Handle pricing based on PriceType
        if (formData.priceType === 'PerPerson') {
          // Convert ageBracketPrices object to dictionary format expected by API
          formData.ageBracketPrices = {};
          this.ageBrackets.forEach(bracket => {
            const price = this.experience.ageBracketPrices[bracket.key] || 0;
            if (price > 0) {
              formData.ageBracketPrices[bracket.key] = parseFloat(price);
            }
          });
          // Set price to 0 when using PerPerson pricing
          formData.price = 0;
        } else {
          // Remove ageBracketPrices when PriceType is PerExperience
          delete formData.ageBracketPrices;
        }
        
        // Process images for API: separate existing paths from new uploads
        if (formData.images && Array.isArray(formData.images)) {
          const processedImages = [];
          const currentImages = [];

          const root = process.env.VUE_APP_URL_ROOT || '';

          formData.images.forEach((image) => {
            if (typeof image === 'string') {
              // Existing image path from API, keep it
              currentImages.push(image);
              return;
            }

            // Object with base64 data (new upload)
            if (image && image.data) {
              processedImages.push({
                Name: image.name || 'image.jpg',
                Data: image.data,
                Mime: image.mime || 'image/jpeg',
                // Field read by LocalIOService; the fallback covers objects built before this change
                Base64: image.base64 || image.data
              });
              return;
            }

            // Object with only url (existing image shown by GalleryPicker)
            if (image && typeof image.url === 'string' && image.url) {
              let url = image.url;
              // Convert absolute URL to relative API path
              if (root && url.startsWith(root)) {
                url = url.substring(root.length);
              }
              // Ensure it looks like a server file path
              if (url.startsWith('/')) {
                currentImages.push(url);
              }
            }
          });

          formData.images = processedImages; // new uploads
          formData.currentImages = currentImages; // images to keep (camelCase for binder)
        }
        
        if (this.experienceId) {
          await this.update({ id: this.experienceId, ...formData });
        } else {
          await this.create(formData);
        }
        
        this.$emit('form-submitted');
        this.$store.dispatch('alert/setAlert', {
          type: 'success',
          message: this.$t('experiences.saveSuccess')
        });
        
      } catch (error) {
        console.error('Error saving experience:', error);
        this.$store.dispatch('alert/setAlert', {
          type: 'error',
          message: this.$t('experiences.saveError')
        });
      } finally {
        this.loading = false;
      }
    },
    
    validateForm() {
      let isValid = true;
      this.errorOutput = {};
      
      // Helper function to check if HTML content is empty
      const isEmptyHtml = (html) => {
        if (!html || typeof html !== 'string') return true;
        const textContent = html.replace(/<[^>]*>/g, '').trim();
        return textContent === '';
      };
      
      // Images are optional when editing; allow zero images
      
      // Validate names for current language
      if (!this.experience.names[this.currentLanguage.key] || 
          isEmptyHtml(this.experience.names[this.currentLanguage.key])) {
        this.errorOutput['Names'] = this.$t('validation.required');
        isValid = false;
      }
      
      // Validate descriptions for current language
      if (!this.experience.descriptions[this.currentLanguage.key] || 
          isEmptyHtml(this.experience.descriptions[this.currentLanguage.key])) {
        this.errorOutput['Descriptions'] = this.$t('validation.required');
        isValid = false;
      }
      
      // If creating a new experience (not editing), both Portuguese and English translations are mandatory
      if (!this.experienceId) {
        // Validate Portuguese name
        const portugueseNameEmpty = !this.experience.names['pt'] || isEmptyHtml(this.experience.names['pt']);
        // Validate English name
        const englishNameEmpty = !this.experience.names['en'] || isEmptyHtml(this.experience.names['en']);
        
        if (portugueseNameEmpty || englishNameEmpty) {
          // Show appropriate error message based on current language
          if (this.currentLanguage.key === 'en' && portugueseNameEmpty) {
            this.errorOutput['Names'] = this.$t('validation.required');
          } else if (this.currentLanguage.key === 'pt' && englishNameEmpty) {
            this.errorOutput['Names'] = this.$t('validation.englishNameRequired');
          } else if (portugueseNameEmpty && englishNameEmpty) {
            this.errorOutput['Names'] = this.$t('validation.required');
          } else if (portugueseNameEmpty) {
            this.errorOutput['Names'] = this.$t('validation.required');
          } else if (englishNameEmpty) {
            this.errorOutput['Names'] = this.$t('validation.englishNameRequired');
          }
          isValid = false;
        }
        
        // Validate Portuguese description
        const portugueseDescriptionEmpty = !this.experience.descriptions['pt'] || isEmptyHtml(this.experience.descriptions['pt']);
        // Validate English description
        const englishDescriptionEmpty = !this.experience.descriptions['en'] || isEmptyHtml(this.experience.descriptions['en']);
        
        if (portugueseDescriptionEmpty || englishDescriptionEmpty) {
          // Show appropriate error message based on current language
          if (this.currentLanguage.key === 'en' && portugueseDescriptionEmpty) {
            this.errorOutput['Descriptions'] = this.$t('validation.required');
          } else if (this.currentLanguage.key === 'pt' && englishDescriptionEmpty) {
            this.errorOutput['Descriptions'] = this.$t('validation.englishDescriptionRequired');
          } else if (portugueseDescriptionEmpty && englishDescriptionEmpty) {
            this.errorOutput['Descriptions'] = this.$t('validation.required');
          } else if (portugueseDescriptionEmpty) {
            this.errorOutput['Descriptions'] = this.$t('validation.required');
          } else if (englishDescriptionEmpty) {
            this.errorOutput['Descriptions'] = this.$t('validation.englishDescriptionRequired');
          }
          isValid = false;
        }
      }
      
      return isValid;
    },
    
    genericValidator(value) {
      if (value === null || value === undefined) {
        return false;
      }
      
      // Para strings simples
      if (typeof value === 'string') {
        return value.trim() !== '';
      }
      
      // Para arrays (como images)
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      
      // Para objetos (como names, descriptions)
      if (typeof value === 'object') {
        const currentValue = value[this.currentLanguage.key];
        if (typeof currentValue === 'string') {
          // Remove HTML tags e verifica se há conteúdo real
          const textContent = currentValue.replace(/<[^>]*>/g, '').trim();
          return textContent !== '';
        }
        return false;
      }
      
      return true;
    },
    
    errorValidator(field) {
      return !this.errorOutput[field];
    },
    
    cancelForm() {
      router.push({ name: 'Experiences' });
    },
    
    onCloseEnglishValidationDialog() {
      this.showEnglishValidationDialog = false;
    }
  }
}
</script>

<style scoped>
.no-slots-alert {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.experience-form-container {
  display: flex;
  gap: 2rem;
}

.experience-image-section {
  flex: 0 0 300px;
}

.experience-form-section {
  flex: 1;
}

.gallery-picker {
  width: 100%;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #dee2e6;
}

@media (max-width: 768px) {
  .experience-form-container {
    flex-direction: column;
  }
  
  .experience-image-section {
    flex: none;
  }
}
</style>

